// src/pages/SystemPreferences.jsx
import React, { useState, useEffect } from "react";
import { Settings, Globe, Clock, Palette, Database, Shield, LogOut } from "lucide-react";

/*
  System Preferences Page
  - General settings: app name, timezone, language, currency
  - Theme & Branding: primary color, logo upload (local preview)
  - Security & Session: session timeout, 2FA toggle, IP whitelist (local list)
  - Persistence in localStorage
  - Save / Reset / Export / Import (JSON)
*/

const STORAGE_KEY = "system_preferences_v1";

const defaults = {
  appName: "Admin Portal",
  primaryColor: "#0d9488", // teal-600
  language: "en",
  timezone: "Asia/Kolkata",
  currency: "INR",
  sessionTimeoutMinutes: 30,
  enable2FA: false,
  ipWhitelist: [],
  logoDataUrl: null,
  allowApiAccess: true,
  auditLogRetentionDays: 90,
};

function useLocalStorage(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);
  return [state, setState];
}

export default function SystemPreferences() {
  const [prefs, setPrefs] = useLocalStorage(STORAGE_KEY, defaults);
  const [logoPreview, setLogoPreview] = useState(prefs.logoDataUrl || null);
  const [exportData, setExportData] = useState(null);
  const [importRaw, setImportRaw] = useState("");

  useEffect(()=> {
    setLogoPreview(prefs.logoDataUrl || null);
  }, [prefs.logoDataUrl]);

  const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const onLogoUpload = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      update({ logoDataUrl: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset preferences to defaults?")) return;
    setPrefs(defaults);
    setExportData(null);
  };

  const exportJson = () => {
    const json = JSON.stringify(prefs, null, 2);
    setExportData(json);
    // trigger download
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `system-preferences-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = () => {
    try {
      const data = JSON.parse(importRaw);
      setPrefs((p)=> ({ ...p, ...data }));
      setImportRaw("");
      alert("Import successful");
    } catch (e) {
      alert("Invalid JSON");
    }
  };

  const removeIp = (ip) => update({ ipWhitelist: prefs.ipWhitelist.filter(x=>x!==ip) });
  const addIp = (ip) => {
    if (!ip) return;
    if (prefs.ipWhitelist.includes(ip)) return alert("Already whitelisted");
    update({ ipWhitelist: [...prefs.ipWhitelist, ip] });
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50 font-roboto-poppins">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
          .font-roboto-poppins { font-family: 'Roboto', 'Poppins', sans-serif; }
        `}
      </style>

      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
            <Settings className="text-teal-600" />
            System Preferences
          </h1>
          <p className="text-sm text-gray-500 mt-1">Core platform configuration & security controls.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* General */}
            <section className="bg-white rounded-xl p-5 shadow">
              <h2 className="font-medium text-gray-700 mb-3">General</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Application Name</label>
                  <input value={prefs.appName} onChange={(e)=>update({appName:e.target.value})} className="mt-1 w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Primary Color</label>
                  <input type="color" value={prefs.primaryColor} onChange={(e)=>update({primaryColor:e.target.value})} className="mt-1 p-1 border rounded w-20 h-10" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Language</label>
                  <select value={prefs.language} onChange={(e)=>update({language:e.target.value})} className="mt-1 p-2 border rounded w-full">
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="es">Spanish</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Timezone</label>
                  <input value={prefs.timezone} onChange={(e)=>update({timezone:e.target.value})} className="mt-1 p-2 border rounded w-full" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Currency</label>
                  <input value={prefs.currency} onChange={(e)=>update({currency:e.target.value})} className="mt-1 p-2 border rounded w-full" />
                </div>
              </div>
            </section>

            {/* Branding */}
            <section className="bg-white rounded-xl p-5 shadow">
              <h2 className="font-medium text-gray-700 mb-3">Branding & Theme</h2>
              <div className="flex items-center gap-4">
                <div className="w-32 h-32 rounded-lg border flex items-center justify-center overflow-hidden bg-gray-50">
                  {logoPreview ? <img src={logoPreview} alt="logo" className="object-contain w-full h-full" /> : <div className="text-xs text-gray-400">No logo</div>}
                </div>
                <div className="space-y-2 flex-1">
                  <label className="block text-sm text-gray-600">Upload Logo</label>
                  <input type="file" accept="image/*" onChange={(e)=>onLogoUpload(e.target.files?.[0])} />
                  <div className="text-xs text-gray-500">SVG/PNG recommended. Max 200KB (client-side only).</div>
                </div>
              </div>
            </section>

            {/* Security & Sessions */}
            <section className="bg-white rounded-xl p-5 shadow">
              <h2 className="font-medium text-gray-700 mb-3">Security & Sessions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Session Timeout (mins)</label>
                  <input type="number" min={1} value={prefs.sessionTimeoutMinutes} onChange={(e)=>update({sessionTimeoutMinutes: Math.max(1, Number(e.target.value))})} className="mt-1 p-2 border rounded w-full" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Audit Log Retention (days)</label>
                  <input type="number" min={0} value={prefs.auditLogRetentionDays} onChange={(e)=>update({auditLogRetentionDays: Math.max(0, Number(e.target.value))})} className="mt-1 p-2 border rounded w-full" />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">Enable Two-Factor Authentication (2FA)</label>
                  <div className="mt-2">
                    <label className="inline-flex items-center gap-3">
                      <input type="checkbox" checked={prefs.enable2FA} onChange={(e)=>update({enable2FA:e.target.checked})} />
                      <span className="text-sm">{prefs.enable2FA ? "2FA Enabled" : "2FA Disabled"}</span>
                    </label>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">IP Whitelist (one per line)</label>
                  <textarea rows={3} value={prefs.ipWhitelist.join("\n")} onChange={(e)=>update({ipWhitelist: e.target.value.split(/\n/).map(s=>s.trim()).filter(Boolean)})} className="mt-1 p-2 border rounded w-full" />
                  <div className="text-xs text-gray-400 mt-1">Optional — leave empty to allow all IPs.</div>
                </div>
              </div>
            </section>

            {/* API & Misc */}
            <section className="bg-white rounded-xl p-5 shadow">
              <h2 className="font-medium text-gray-700 mb-3">API & Integrations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Allow API Access</label>
                  <div className="mt-2">
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={prefs.allowApiAccess} onChange={(e)=>update({allowApiAccess:e.target.checked})} />
                      <span className="text-sm">{prefs.allowApiAccess ? "Enabled" : "Disabled"}</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">API Rate Limit (per minute)</label>
                  <input type="number" min={0} value={1000} disabled className="mt-1 p-2 border rounded w-full bg-gray-50" />
                  <div className="text-xs text-gray-400 mt-1">Managed at platform level.</div>
                </div>
              </div>
            </section>

            <div className="flex gap-3 justify-end">
              <button onClick={resetDefaults} className="px-4 py-2 bg-gray-100 rounded">Reset</button>
              <button onClick={exportJson} className="px-4 py-2 bg-white border rounded">Export</button>
              <button onClick={()=>alert('Saved (local)')} className="px-4 py-2 bg-teal-600 text-white rounded">Save Preferences</button>
            </div>
          </div>

          {/* RIGHT: quick info */}
          <aside className="space-y-6">
            <div className="bg-white rounded-xl p-4 shadow">
              <h3 className="font-medium text-gray-700">Quick Info</h3>
              <div className="text-sm text-gray-500 mt-2">
                <div className="flex items-center gap-2"><Globe /> Language & timezone</div>
                <div className="flex items-center gap-2 mt-2"><Clock /> Session timeout: {prefs.sessionTimeoutMinutes} min</div>
                <div className="flex items-center gap-2 mt-2"><Shield /> 2FA: {prefs.enable2FA ? "Enabled" : "Disabled"}</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow">
              <h3 className="font-medium text-gray-700">Import Preferences</h3>
              <div className="mt-2">
                <textarea rows={6} value={importRaw} onChange={(e)=>setImportRaw(e.target.value)} className="w-full p-2 border rounded" placeholder='Paste JSON here to import' />
                <div className="flex gap-2 justify-end mt-2">
                  <button onClick={()=>{ setImportRaw(""); }} className="px-3 py-1 bg-gray-100 rounded">Clear</button>
                  <button onClick={importJson} className="px-3 py-1 bg-teal-600 text-white rounded">Import</button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow">
              <h3 className="font-medium text-gray-700">Logo</h3>
              <div className="mt-2">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className="w-full object-contain rounded" />
                ) : (
                  <div className="text-sm text-gray-400">No logo uploaded</div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
